import { useEffect, useState } from 'react'
import {
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dropdown,
  Option,
  Input,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { LICENSE_ICONS, getLicenseIcon, DEFAULT_ICON_KEY } from '../lib/licenseIcons'
import type { LicenseTypesResponse } from '../types'

export default function LicenseTypesTab(props: { serviceId: string; refreshKey: number }) {
  const { serviceId, refreshKey } = props
  const [types, setTypes] = useState<LicenseTypesResponse['types']>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    apiFetchJson<LicenseTypesResponse>(`/api/license-types?serviceId=${serviceId}`)
      .then(res => setTypes(res.types))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [serviceId, refreshKey])

  async function saveLicenseTypeConfig(
    licenseType: string,
    patch: { iconKey?: string; displayName?: string; totalSeats?: number | null; costPerSeat?: number | null },
  ) {
    try {
      await apiFetchJson('/api/license-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serviceId, licenseType, ...patch }),
      })
    } catch (e) {
      setError(String(e))
    }
  }

  function handleIconChange(licenseType: string, iconKey: string) {
    setTypes(prev => prev.map(t => (t.licenseType === licenseType ? { ...t, iconKey } : t)))
    saveLicenseTypeConfig(licenseType, { iconKey })
  }

  function handleDisplayNameEdit(licenseType: string, displayName: string) {
    setTypes(prev => prev.map(t => (t.licenseType === licenseType ? { ...t, displayName } : t)))
  }

  function handleDisplayNameCommit(licenseType: string, displayName: string) {
    saveLicenseTypeConfig(licenseType, { displayName })
  }

  function handleTotalSeatsEdit(licenseType: string, raw: string) {
    const totalSeats = raw === '' ? undefined : Number(raw)
    setTypes(prev => prev.map(t => (t.licenseType === licenseType ? { ...t, totalSeats } : t)))
  }

  // Sends explicit `null` rather than omitting the key when cleared — Merge mode drops omitted
  // properties instead of clearing them (see LicenseTypeConfig's doc comment on the API side).
  function handleTotalSeatsCommit(licenseType: string, raw: string) {
    saveLicenseTypeConfig(licenseType, { totalSeats: raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw) })
  }

  function handleCostPerSeatEdit(licenseType: string, raw: string) {
    const costPerSeat = raw === '' ? undefined : Number(raw)
    setTypes(prev => prev.map(t => (t.licenseType === licenseType ? { ...t, costPerSeat } : t)))
  }

  function handleCostPerSeatCommit(licenseType: string, raw: string) {
    saveLicenseTypeConfig(licenseType, { costPerSeat: raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw) })
  }

  if (loading) return <Spinner label="Loading license types..." />
  if (error) return <p>Error: {error}</p>
  if (types.length === 0) return <p>No license types yet — run a sync first.</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHeaderCell>License type (raw)</TableHeaderCell>
          <TableHeaderCell>Display name</TableHeaderCell>
          <TableHeaderCell>Icon</TableHeaderCell>
          <TableHeaderCell>Total purchased</TableHeaderCell>
          <TableHeaderCell>Cost/seat</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {types.map(t => {
          const Icon = getLicenseIcon(t.iconKey)
          const selected = t.iconKey ?? DEFAULT_ICON_KEY
          return (
            <TableRow key={t.licenseType}>
              <TableCell>{t.licenseType}</TableCell>
              <TableCell>
                <Input
                  value={t.displayName ?? ''}
                  placeholder={t.licenseType}
                  onChange={(_e, d) => handleDisplayNameEdit(t.licenseType, d.value)}
                  onBlur={e => handleDisplayNameCommit(t.licenseType, e.target.value)}
                  style={{ minWidth: 220 }}
                />
              </TableCell>
              <TableCell>
                <Dropdown
                  value={selected}
                  selectedOptions={[selected]}
                  onOptionSelect={(_e, optionData) => {
                    if (optionData.optionValue) handleIconChange(t.licenseType, optionData.optionValue)
                  }}
                  style={{ minWidth: 180 }}
                >
                  {Object.keys(LICENSE_ICONS).map(key => (
                    <Option key={key} value={key}>
                      {key}
                    </Option>
                  ))}
                </Dropdown>
                <Icon style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={t.totalSeats != null ? String(t.totalSeats) : ''}
                  placeholder="Unlimited"
                  onChange={(_e, d) => handleTotalSeatsEdit(t.licenseType, d.value)}
                  onBlur={e => handleTotalSeatsCommit(t.licenseType, e.target.value)}
                  style={{ minWidth: 110 }}
                />
              </TableCell>
              <TableCell>
                <Input
                  type="number"
                  value={t.costPerSeat != null ? String(t.costPerSeat) : ''}
                  placeholder="Free"
                  onChange={(_e, d) => handleCostPerSeatEdit(t.licenseType, d.value)}
                  onBlur={e => handleCostPerSeatCommit(t.licenseType, e.target.value)}
                  style={{ minWidth: 110 }}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
